import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { ActionCardComponent } from '../../../shared/components/action-card/action-card.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { StatusInventarioEnum } from '../../../shared/enums/suprimentos/status-inventario.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { InventarioService } from '../../../shared/services/apis/suprimentos/inventario.service';
import { InventarioConsultaOrquestradorService } from '../../../shared/services/apis/orquestrador-suprimentos/inventario-consulta-orquestrador.service';
import { InventarioLookup } from '../../../shared/services/models/suprimentos/inventario-lookup.model';
import { InventarioListStruct } from '../../../shared/services/structs/suprimentos/inventario-list.struct';
import { UtilService } from '../../../shared/services/util.service';
import { InventarioResumoModalComponent } from './dialogs/inventario-resumo-modal/inventario-resumo-modal.component';

@Component({
  selector: 'app-inventario-lista',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    ReactiveFormsModule,
    MatBadgeModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatMenuModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
    ActionCardComponent,
    RouterModule,
  ],
  templateUrl: './inventario-lista.component.html',
  styleUrls: ['./inventario-lista.component.css'],
})
export class InventarioListaComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_InventarioEstoque;
  public _StatusInventarioEnum = StatusInventarioEnum;

  public model: FormGroup;
  public lookup: InventarioLookup = { statusList: [], tipoOrigemList: [], tipoValorBaseList: [] };

  public _isLoading: boolean = false;
  public _isLoadingLookup: boolean = false;
  public _dataSource = new MatTableDataSource<InventarioListStruct>();
  public _paginaAtual: number = 0;
  public _ultimaPagina: boolean = false;
  public _totalRegistros: number = 0;

  public displayedColumns: string[] = ['descricao', 'status', 'origem', 'dataInclusao', 'dataConclusao', 'actions'];

  constructor(
    private formBuilder: FormBuilder,
    private inventarioService: InventarioService,
    private inventarioConsultaService: InventarioConsultaOrquestradorService,
    private alertService: AlertService,
    private utilService: UtilService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  public voltar(): void {
    this.router.navigate(['/suprimentos/controle-estoque']);
  }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      descricao: [''],
      idStatusInventario: [null],
      idTipoOrigemInventario: [null],
    });

    this.carregarLookup();
    this.loadFilters();
    this.pesquisar();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/suprimentos/inventario',
    ]);
  }

  public pesquisar(): void {
    if (this._isLoading) return;

    this._isLoading = true;
    const { descricao, idStatusInventario, idTipoOrigemInventario } = this.model.value;
    const pagina = this._paginaAtual + 1;

    this.inventarioConsultaService.getAll(idStatusInventario, idTipoOrigemInventario, descricao, pagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        const novosItens = response.listaInventarios || [];

        if (this._paginaAtual === 0) {
          this._dataSource.data = novosItens;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...novosItens];
        }

        this._totalRegistros = response.totalRegistros;
        this._ultimaPagina = !novosItens.length || this._dataSource.data.length >= response.totalRegistros;
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
      },
    });
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this._dataSource.data = [];
    this._ultimaPagina = false;
    this.pesquisar();
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  public aplicarFiltros(): void {
    this.clickPesquisar();
  }

  public limparFiltros(): void {
    this.model.patchValue({ descricao: '', idStatusInventario: null, idTipoOrigemInventario: null });
    this.clickPesquisar();
  }

  public get qtdFiltrosAvancados(): number {
    const { idStatusInventario, idTipoOrigemInventario } = this.model.value;
    return (idStatusInventario != null ? 1 : 0) + (idTipoOrigemInventario != null ? 1 : 0);
  }

  public executarAcaoIndisponivel(rotina: string): void {
    this.alertService.show('Atenção', `${rotina} será implementado em uma próxima etapa.`, AlertType.warning);
  }

  public getStatusClass(idStatus: number): string {
    if (idStatus === StatusInventarioEnum.Pendente) return 'badge-amarelo';
    if (idStatus === StatusInventarioEnum.EmProcessamento) return 'badge-azul';
    if (idStatus === StatusInventarioEnum.Falha) return 'badge-vermelho';
    if (idStatus === StatusInventarioEnum.Concluido) return 'badge-verde';
    if (idStatus === StatusInventarioEnum.AguardandoProcessamento) return 'badge-laranja';
    return 'badge-cinza';
  }

  public isPendente(row: InventarioListStruct): boolean {
    return row.idStatusInventario === StatusInventarioEnum.Pendente;
  }

  public isConcluido(row: InventarioListStruct): boolean {
    return row.idStatusInventario === StatusInventarioEnum.Concluido;
  }

  public verResumo(row: InventarioListStruct): void {
    this.dialog.open(InventarioResumoModalComponent, {
      data: { idInventario: row.idInventario, datetimeConclusao: row.datetimeConclusao },
      width: '1100px',
      maxWidth: '95vw',
    });
  }

  public excluir(row: InventarioListStruct): void {
    if (!this.isPendente(row)) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmação',
        description: `Deseja realmente excluir o inventário "${row.descricao}"? Essa ação não pode ser desfeita.`,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;

      this.inventarioService.deletar(row.idInventario).subscribe({
        next: res => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription, AlertType.error);
            return;
          }
          this.alertService.show('Sucesso', 'Inventário excluído.', AlertType.success);
          this.clickPesquisar();
        },
        error: err => {
          this.alertService.show('Erro inesperado', err?.message || err, AlertType.error);
        },
      });
    });
  }

  public exportar(row: InventarioListStruct): void {
    if (!this.isConcluido(row)) return;

    this.inventarioConsultaService.getDigitacao(row.idInventario).subscribe({
      next: res => {
        if (res.isError || !res.digitacao) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar o inventário.', AlertType.error);
          return;
        }

        const sep = ';';
        const campo = (v: string | null | undefined): string => {
          const texto = v ?? '';
          return /[";\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
        };
        const qtd = (v: number | null | undefined): string => (v == null ? '' : Number(v).toFixed(2));

        const header = [
          'Nome do produto',
          'Código do produto (SKU)',
          'Unidade de medida',
          'Categoria',
          'Custo Médio',
          'Local de Estoque',
          'Quant. inicial',
          'Quant. inventariada',
          'Ajuste no Estoque',
        ].join(sep);

        const rows = res.digitacao.localizacoes.flatMap(loc =>
          loc.itens.map(i =>
            [
              campo(i.nomeProduto),
              campo(i.skuProduto),
              campo(i.unidadeMedida),
              campo(i.nomeCategoriaProduto),
              qtd(i.custoMedio),
              campo(loc.nomeLocalizacao),
              qtd(i.saldoSistema),
              qtd(i.saldoContado),
              qtd(i.ajusteEstoque),
            ].join(sep)
          )
        );

        const csv = '﻿' + [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const descricaoArquivo = row.descricao.replace(/[^a-zA-Z0-9À-ÿ _-]/g, '').trim().replace(/\s+/g, '-');
        anchor.href = url;
        anchor.download = `inventario-${row.idInventario}-${descricaoArquivo}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
      },
      error: err => {
        this.alertService.show('Erro inesperado', err?.message || err, AlertType.error);
      },
    });
  }

  private carregarLookup(): void {
    this._isLoadingLookup = true;
    this.inventarioService.getLookup().subscribe({
      next: res => {
        this._isLoadingLookup = false;
        if (!res.isError && res.lookup) {
          this.lookup = res.lookup;
        }
      },
      error: () => {
        this._isLoadingLookup = false;
      },
    });
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }
}
